import { useEffect, useState } from 'react';
import { _notNil } from '@/littledash';
import type {
  DataTableCellIdCoordinate,
  DataTableCellReference,
  DataTableCellState,
  DataTableCellStateChangeEvent,
  DataTableCellUpdateEvent,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '../DataTableProvider';

export interface UseCellStateReturn {
  cellState: DataTableCellState;
  tooltip: string | undefined;
}

/**
 * Subscribe to cell validation state and tooltip messages
 *
 * This hook manages cell validation state (valid/invalid/warning/loading) and
 * extracts error messages for tooltip display. Uses AbortController for cleanup.
 *
 * @param idCoordinate - Cell ID coordinate (API IDs)
 * @param cellRef - Cell reference string for event filtering
 * @returns Object containing cell state and tooltip message
 */
export const useCellState = (
  idCoordinate: DataTableCellIdCoordinate | undefined,
  cellRef: DataTableCellReference
): UseCellStateReturn => {
  const { dataTableService } = useDataTable();
  const [cellState, setCellState] = useState<DataTableCellState>(() =>
    _notNil(idCoordinate) ? dataTableService.cellState(idCoordinate) : 'Valid'
  );
  const [tooltip, setTooltip] = useState<string | undefined>(() => {
    if (_notNil(idCoordinate)) {
      const status = dataTableService.cellStatus(idCoordinate);
      return 'error' in status ? status.error : undefined;
    }
    return undefined;
  });

  useEffect(() => {
    if (_notNil(idCoordinate)) {
      const abortController = new AbortController();

      // Initialize state
      setCellState(dataTableService.cellState(idCoordinate));
      const initialStatus = dataTableService.cellStatus(idCoordinate);
      setTooltip('error' in initialStatus ? initialStatus.error : undefined);

      const cellStateChangeListener = (event: DataTableCellStateChangeEvent) => {
        setCellState((previousCellState) => {
          if (event.detail.invalidCells.has(cellRef) || previousCellState !== 'Valid') {
            const updatedCellState = dataTableService.cellState(idCoordinate);
            if (updatedCellState === 'Invalid' || updatedCellState === 'Warning') {
              const status = dataTableService.cellStatus(idCoordinate);
              setTooltip('error' in status ? status.error : undefined);
            } else {
              setTooltip(undefined);
            }
            return updatedCellState;
          }
          return previousCellState;
        });
      };

      const cellUpdateListener = (event: DataTableCellUpdateEvent) => {
        if (Object.prototype.hasOwnProperty.call(event.detail, cellRef)) {
          const updatedCellState = dataTableService.cellState(idCoordinate);
          setCellState(updatedCellState);
        }
      };

      dataTableService.subscribe(DataTableEvent.CellStateChange, cellStateChangeListener, abortController.signal);
      dataTableService.subscribe(DataTableEvent.CellUpdate, cellUpdateListener, abortController.signal);

      return () => {
        abortController.abort();
      };
    }
  }, [dataTableService, idCoordinate, cellRef]);

  return { cellState, tooltip };
};
